import { api } from '../api/client.js';
import { GOLD_MONTHLY_BRL, PLAN_COPY } from '../config/pricing.js';

/**
 * @typedef {{
 *   name: string,
 *   subtitle: string,
 *   priceLabel: string,
 *   cadence?: string,
 *   afterPriceLabel?: string,
 *   monthlyAmountBrl?: number,
 *   bullets: string[],
 *   caveat?: string,
 *   featured?: boolean,
 * }} PlanCopyView
 */

/**
 * @param {unknown} amount
 * @returns {string}
 */
export function formatPlanMonthlyLabel(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return n.toFixed(2);
}

/**
 * @param {{ price_label?: string|null, monthly_amount_brl?: string|number|null }} row
 * @returns {string}
 */
export function resolvePriceLabel(row) {
  const label = typeof row?.price_label === 'string' ? row.price_label.trim() : '';
  if (label !== '') return label;
  return `R$ ${formatPlanMonthlyLabel(row?.monthly_amount_brl)}`;
}

/**
 * Quando há price_label (ex.: «Grátis por 2 dias») e mensalidade > 0, mostra o valor depois do teste.
 * Cadence preenchido no admin tem prioridade sobre o texto automático.
 *
 * @param {{
 *   price_label?: string|null,
 *   cadence?: string|null,
 *   monthly_amount_brl?: string|number|null,
 * }} row
 * @returns {string}
 */
export function resolveAfterPriceLabel(row) {
  const monthly = Number(row?.monthly_amount_brl);
  if (!Number.isFinite(monthly) || monthly <= 0) return '';
  const cadence = typeof row?.cadence === 'string' ? row.cadence.trim() : '';
  if (cadence !== '') return cadence;
  const priceLabel = typeof row?.price_label === 'string' ? row.price_label.trim() : '';
  if (priceLabel === '') return '';
  return `depois R$ ${formatPlanMonthlyLabel(monthly)}/mês`;
}

/**
 * @param {unknown} plans
 * @returns {{ trial: PlanCopyView, gold: PlanCopyView }}
 */
export function mapPlansToCopy(plans) {
  const list = Array.isArray(plans) ? plans : [];
  /** @param {string} key */
  const find = (key) => list.find((p) => p && typeof p === 'object' && p.plan_key === key);

  const trialRow = find('essential_trial');
  const goldRow = find('gold');

  /** @type {PlanCopyView} */
  const trial = trialRow
    ? {
        name: String(trialRow.lp_name || PLAN_COPY.trial.name),
        subtitle: String(trialRow.subtitle || PLAN_COPY.trial.subtitle),
        priceLabel: resolvePriceLabel(trialRow),
        afterPriceLabel: resolveAfterPriceLabel(trialRow),
        monthlyAmountBrl: Number(trialRow.monthly_amount_brl) || 0,
        bullets: Array.isArray(trialRow.bullets) && trialRow.bullets.length
          ? trialRow.bullets.map(String)
          : [...PLAN_COPY.trial.bullets],
        caveat:
          trialRow.caveat != null && String(trialRow.caveat).trim() !== ''
            ? String(trialRow.caveat)
            : PLAN_COPY.trial.caveat,
      }
    : { ...PLAN_COPY.trial, bullets: [...PLAN_COPY.trial.bullets], afterPriceLabel: '' };

  /** @type {PlanCopyView} */
  const gold = goldRow
    ? {
        name: String(goldRow.lp_name || PLAN_COPY.gold.name),
        subtitle: String(goldRow.subtitle || PLAN_COPY.gold.subtitle),
        priceLabel: resolvePriceLabel(goldRow),
        cadence:
          typeof goldRow.cadence === 'string' && goldRow.cadence !== ''
            ? goldRow.cadence
            : PLAN_COPY.gold.cadence,
        monthlyAmountBrl: Number(goldRow.monthly_amount_brl) || 0,
        featured: Boolean(goldRow.featured),
        bullets: Array.isArray(goldRow.bullets) && goldRow.bullets.length
          ? goldRow.bullets.map(String)
          : [...PLAN_COPY.gold.bullets],
      }
    : {
        ...PLAN_COPY.gold,
        bullets: [...PLAN_COPY.gold.bullets],
        priceLabel: PLAN_COPY.gold.priceLabel,
      };

  return { trial, gold };
}

/**
 * @param {unknown} plans
 * @returns {number}
 */
export function getGoldMonthlyBrl(plans) {
  const list = Array.isArray(plans) ? plans : [];
  const gold = list.find((p) => p && typeof p === 'object' && p.plan_key === 'gold');
  if (!gold) return GOLD_MONTHLY_BRL;
  const n = Number(gold.monthly_amount_brl);
  return Number.isFinite(n) ? n : GOLD_MONTHLY_BRL;
}

/**
 * @returns {Promise<{
 *   plans: object[],
 *   copy: { trial: PlanCopyView, gold: PlanCopyView },
 *   goldMonthlyBrl: number,
 * }>}
 */
export async function fetchPublicPlanCatalog() {
  try {
    const data = await api('/public/billing/plans', { skipAuth: true });
    const plans = Array.isArray(data?.plans) ? data.plans : [];
    if (plans.length === 0) {
      return {
        plans: [],
        copy: {
          trial: { ...PLAN_COPY.trial, bullets: [...PLAN_COPY.trial.bullets] },
          gold: { ...PLAN_COPY.gold, bullets: [...PLAN_COPY.gold.bullets] },
        },
        goldMonthlyBrl: GOLD_MONTHLY_BRL,
      };
    }
    return {
      plans,
      copy: mapPlansToCopy(plans),
      goldMonthlyBrl: getGoldMonthlyBrl(plans),
    };
  } catch {
    return {
      plans: [],
      copy: {
        trial: { ...PLAN_COPY.trial, bullets: [...PLAN_COPY.trial.bullets] },
        gold: { ...PLAN_COPY.gold, bullets: [...PLAN_COPY.gold.bullets] },
      },
      goldMonthlyBrl: GOLD_MONTHLY_BRL,
    };
  }
}
